import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessagesSquare, Send, Loader2, ChevronLeft, Bot } from "lucide-react";
import { api } from "../../lib/api";

const SUGGEST = ["@research Perth strata maintenance rules", "@finance who owes us money", "@social draft next week's posts", "@leads any stale follow-ups?"];

let _mid = 0;
const mid = () => `m${++_mid}`;

export function CommandChat({ onAction }) {
  const [open, setOpen] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { api.get("/agent/chat/history").then((r) => setMessages((r.data || []).map((m) => ({ ...m, id: mid() })))).catch((e) => console.error("Failed to load chat history", e)); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading, open]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setMessages((m) => [...m, { id: mid(), role: "user", text: msg }]);
    setInput(""); setLoading(true);
    try {
      const r = await api.post("/agent/chat", { message: msg });
      setMessages((m) => [...m, { id: mid(), role: "agent", agent: r.data.agent, text: r.data.text }]);
      onAction && onAction();
    } catch (e) {
      console.error("Command chat send failed", e);
      setMessages((m) => [...m, { id: mid(), role: "agent", agent: "System", text: "Something went wrong — try again." }]);
    } finally { setLoading(false); }
  };

  if (!open) {
    return (
      <button data-testid="command-chat-open" onClick={() => setOpen(true)}
        className="fixed left-4 bottom-4 z-40 grid place-items-center w-12 h-12 rounded-full bg-[#62B848] text-white shadow-xl">
        <MessagesSquare size={22} />
      </button>
    );
  }

  return (
    <aside data-testid="command-chat" className="hidden xl:flex fixed left-64 top-0 bottom-0 w-[340px] z-30 flex-col bg-[#14201a] border-r border-white/10 text-white">
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-white/10">
        <div className="flex items-center gap-2"><Bot size={18} className="text-[#62B848]" /><span className="font-display font-bold text-sm">Command Chat</span></div>
        <button data-testid="command-chat-close" onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><ChevronLeft size={18} /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
        {messages.length === 0 && (
          <div className="text-white/50 text-sm px-1">
            <p className="mb-3">Talk to your crew. The Chief of Staff routes, or @mention an agent.</p>
            {SUGGEST.map((s) => (
              <button key={s} onClick={() => send(s)} className="block w-full text-left text-xs bg-white/5 hover:bg-white/10 rounded-lg px-3 py-2 mb-1.5 text-white/70">{s}</button>
            ))}
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#62B848] text-[#0c1a12] rounded-br-md" : "bg-white/[0.06] border border-white/10 rounded-bl-md"}`}>
              {m.role === "agent" && <div className="text-[10px] font-bold uppercase tracking-wide text-[#62B848] mb-1">{m.agent}</div>}
              {m.text}
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-white/[0.06] border border-white/10 px-4 py-3 rounded-2xl"><Loader2 size={16} className="animate-spin text-[#62B848]" /></div></div>}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-white/10 flex items-center gap-2">
        <input data-testid="command-chat-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Message your crew…"
          className="flex-1 rounded-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#62B848]" />
        <button data-testid="command-chat-send" type="submit" disabled={loading} className="grid place-items-center w-10 h-10 rounded-full bg-[#62B848] text-white disabled:opacity-50"><Send size={17} /></button>
      </form>
    </aside>
  );
}
